import { useMutation, useQueryClient } from '@tanstack/react-query';
import { carrierExclusionListKey } from '../lib/queryKeys';
import type { AddExcludedUserOutcome } from '../lib/excludedUser';

export type AddOutcome = AddExcludedUserOutcome & { phone: string };

export interface UseAddExcludedUser {
  add: (phone: string) => void;
  isAdding: boolean;
  // The last answer, with the phone it answers about, so the card can tell a stale message from a
  // live one.
  outcome: AddOutcome | null;
  // A failure that is not an outcome: network, 5xx, a refused slug. Worded with fixed copy by the card.
  isError: boolean;
}

// The write AWAITS its own invalidation, like the door-access batch: the button is not idle until
// the table can show the row. A failed write invalidates too, so the screen never asserts a state
// the server does not hold. Never retried: a refused write must not be silently re-sent.
export function useAddExcludedUser(
  slug: string,
  addExcludedUser: (slug: string, phone: string) => Promise<AddExcludedUserOutcome>,
  options: { onAdded?: () => void } = {},
): UseAddExcludedUser {
  const queryClient = useQueryClient();

  const mutation = useMutation({
    retry: false,
    mutationFn: async (phone: string): Promise<AddOutcome> => {
      const result = await addExcludedUser(slug, phone);
      if (result.kind === 'added') {
        await queryClient.invalidateQueries({ queryKey: carrierExclusionListKey(slug) });
      }
      return { ...result, phone };
    },
    onSuccess: (result) => {
      if (result.kind === 'added') options.onAdded?.();
    },
    onError: () => {
      void queryClient.invalidateQueries({ queryKey: carrierExclusionListKey(slug) });
    },
  });

  return {
    add: (phone) => {
      mutation.mutate(phone);
    },
    isAdding: mutation.isPending,
    outcome: mutation.data ?? null,
    isError: mutation.isError,
  };
}
